import { bodyshopApi } from "@/main/api";
import { KeyValue, KeyValueEnum } from "@/main/models/common";
import { AssociatedSupplierResponse } from "@/main/models/response/bodyshopResponse";
import { HeadQuarterBranchFilterEnum } from "@/main/services/enum/HeadQuarterBranchFilterEnum";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";
import { useCountryReducer } from "@/main/store/country";
import { useStatusRegisterReducer } from "@/main/store/statusRegister";
import React, { createContext, useContext, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

interface IBodyshopRegisterContext {
	hasScreenBranch: boolean;
	setHasScreenBranch: (value: boolean) => void;
	isEdit: boolean;
	isLoadingSave: boolean;
	setIsLoadingSave: (value: boolean) => void;
	setIsEdit: (value: boolean) => void;
	isAddBranch: boolean;
	setIsAddBranch: (value: boolean) => void;
	typeCompany: ITypeCompany;
	changeTypeCompany: (value: ITypeCompany) => void;
	displaySaveBranchWithHeadquarter: (country: string | undefined, headquarterKey: string) => void;
	displayEditBranchWithHeadquarter: (country: string | undefined, guidHeadquarter: string) => void;
	displayEditHeadquarter: () => void;
	displayEditBranch: () => void;
	associatedSuppliers: AssociatedSupplierResponse[];
	setAssociatedSuppliers: (value: AssociatedSupplierResponse[]) => void;
	loadedRegistrationNumber: string;
	setLoadedRegistrationNumber: (value: string) => void;
	loadedSelfSupplied: boolean;
	setLoadedSelfSupplied: (value: boolean) => void;
}

interface IBodyshopRegisterProvider {
	children: React.ReactNode;
}

// Interface to react-hook-form
const INITIAL_FORM_STATE = {
	country: null,
	companyName: "",
	companyNameHeadquarter: null,
	rfc: "",
	status: null,
	tradeName: "",
	bodyhopType: "",
	entityType: null,
	mainSupplier: null,
	selfSupplied: false,
};
export interface IBodyShopRegisterForm {
	country: KeyValue | null | undefined;
	companyName: string;
	companyNameHeadquarter: KeyValue | null;
	rfc: string;
	status: KeyValue | null;
	tradeName: string;
	bodyshopType: KeyValue | null;
	entityType: KeyValueEnum | null;
	observation: string;
	mainSupplier: KeyValue | null;
	selfSupplied: boolean;
}

export type ITypeCompany = "headquarters" | "branch";

const BodyshopRegisterContext = createContext<IBodyshopRegisterContext>({
	hasScreenBranch: false,
	setHasScreenBranch: () => {},
	isLoadingSave: false,
	isEdit: false,
	setIsEdit: () => {},
	setIsLoadingSave: () => {},
	isAddBranch: false,
	setIsAddBranch: () => {},
	typeCompany: "headquarters",
	changeTypeCompany: () => {},
	displaySaveBranchWithHeadquarter: (_country, _headquarterKey) => {},
	displayEditBranchWithHeadquarter: (_country, _guidHeadquarter) => {},
	displayEditHeadquarter: () => {},
	displayEditBranch: () => {},
	associatedSuppliers: [],
	setAssociatedSuppliers: () => {},
	loadedRegistrationNumber: "",
	setLoadedRegistrationNumber: () => {},
	loadedSelfSupplied: false,
	setLoadedSelfSupplied: () => {},
});

function BodyshopRegisterProvider({ children }: IBodyshopRegisterProvider) {
	const methods = useForm<IBodyShopRegisterForm>({
		mode: "onChange",
		defaultValues: INITIAL_FORM_STATE,
	});

	const countryReducer = useCountryReducer();
	const statusReducer = useStatusRegisterReducer();

	const [hasScreenBranch, setHasScreenBranch] = useState(false);
	const [isEdit, setIsEdit] = useState(false);
	const [isLoadingSave, setIsLoadingSave] = useState(false);
	const [isAddBranch, setIsAddBranch] = useState(false);
	const [typeCompany, setTypeCompany] = useState<ITypeCompany>("headquarters");
	const [associatedSuppliers, setAssociatedSuppliers] = useState<AssociatedSupplierResponse[]>([]);
	const [loadedRegistrationNumber, setLoadedRegistrationNumber] = useState<string>("");
	const [loadedSelfSupplied, setLoadedSelfSupplied] = useState<boolean>(false);

	async function displaySaveBranchWithHeadquarter(country: string | undefined, rfcHeadquarter: string) {
		try {
			const countryFinded = countryReducer.countriesUser?.data?.find((item) => item.key === country);
			const statusFinded = statusReducer.statusRegister?.data?.find(
				(item) => item.key === String(StatusRegisterEnum.Active),
			);

			setHasScreenBranch(true);
			setIsAddBranch(true);
			setIsEdit(false);
			setTypeCompany("branch");
			methods.reset({ ...INITIAL_FORM_STATE, status: statusFinded });
			methods.setValue("country", countryFinded);

			const {
				data: { result },
			} = await bodyshopApi.getBodyshopsOrBodyshopBranchesDomain({
				guidCountry: country,
				type: HeadQuarterBranchFilterEnum.Only_Headquarters,
				idStatusRegister: StatusRegisterEnum.Active,
				registrationNumber: rfcHeadquarter,
			});
			const headquartFinded = result[0];
			methods.setValue("companyNameHeadquarter", headquartFinded);
			methods.setValue("rfc", rfcHeadquarter);
			methods.setFocus("companyName");
		} catch {
			setHasScreenBranch(false);
			setIsAddBranch(false);
		}
	}
	async function displayEditBranchWithHeadquarter(country: string | undefined, guidHeadquarter: string) {
		try {
			const countryFinded = countryReducer.countriesUser?.data?.find((item) => item.key === country);
			const statusFinded = statusReducer.statusRegister?.data?.find(
				(item) => item.key === String(StatusRegisterEnum.Active),
			);

			setHasScreenBranch(true);
			setIsAddBranch(false);
			setIsEdit(true);
			setTypeCompany("branch");

			methods.reset({ ...INITIAL_FORM_STATE, status: statusFinded });
			methods.setValue("country", countryFinded);

			const {
				data: { result },
			} = await bodyshopApi.getBodyshopsOrBodyshopBranchesDomain({
				type: HeadQuarterBranchFilterEnum.All,
				guidBodyShop: guidHeadquarter,
			});
			const headquartFinded = result[0];
			methods.setValue("companyNameHeadquarter", headquartFinded);
			methods.setFocus("rfc");
		} catch {
			setHasScreenBranch(false);
			setIsAddBranch(false);
		}
	}

	function displayEditHeadquarter() {
		setIsEdit(true);
		setHasScreenBranch(false);
		setIsAddBranch(false);
		setTypeCompany("headquarters");
	}

	function displayEditBranch() {
		setIsEdit(true);
		setHasScreenBranch(true);
		setIsAddBranch(false);
		setTypeCompany("branch");
	}

	function changeTypeCompany(value: ITypeCompany) {
		setTypeCompany(value);

		if (value === "headquarters") {
			methods.setValue("companyNameHeadquarter", null);
		}
	}

	return (
		<FormProvider {...methods}>
			<BodyshopRegisterContext.Provider
				value={{
					hasScreenBranch,
					isEdit,
					isLoadingSave,
					isAddBranch,
					setHasScreenBranch,
					setIsAddBranch,
					setIsEdit,
					setIsLoadingSave,
					typeCompany,
					changeTypeCompany,
					displaySaveBranchWithHeadquarter,
					displayEditBranchWithHeadquarter,
					displayEditHeadquarter,
					displayEditBranch,
					associatedSuppliers,
					setAssociatedSuppliers,
					loadedRegistrationNumber,
					setLoadedRegistrationNumber,
					loadedSelfSupplied,
					setLoadedSelfSupplied,
				}}
			>
				{children}
			</BodyshopRegisterContext.Provider>
		</FormProvider>
	);
}

const useBodyshopRegisterContext = () => useContext(BodyshopRegisterContext);

export { BodyshopRegisterProvider, useBodyshopRegisterContext };
